import http from '@monolith/legacy/services/api/http';
import { captureException } from '@sentry/browser';

const FEATURE_FLAGS_ENDPOINT = '/api/feature-flags';
const ME_CONFIG_ENDPOINT = '/api/v1/me/config';
const CURRENCIES_RATES_ENDPOINT = '/api/v1/currencies/rates/latest';
const AUTH_IDENTITY_ENDPOINT = '/auth/identity';
const GLOBAL_CONFIG_ENDPOINT = '/api/global-config';
const BRAND_CATEGORIES_ENDPOINT = '/api/brand/categories';

export interface FeatureFlagsResponse {
  features: Record<string, string> | null;
  experiments: Record<string, string> | null;
}

/**
 * Fetch feature flags and experiments from API
 * Single API call returns both exposed_feature_flags and experiments_feature_flags
 * @returns Object with features and experiments, or nulls on error
 */
export async function fetchFeatureFlags(): Promise<FeatureFlagsResponse> {
  try {
    const response = await http().get(FEATURE_FLAGS_ENDPOINT);
    // API returns { data: { exposed_feature_flags: {...}, experiments_feature_flags: {...} } }
    const data = response.data?.data;
    return {
      features: data?.exposed_feature_flags ?? null,
      experiments: data?.experiments_feature_flags ?? null,
    };
  } catch (error) {
    captureException(new Error('Failed to fetch feature flags from API', { cause: error }));
    return { features: null, experiments: null };
  }
}

export interface MeConfigResponse {
  country: string | null;
  currency: string | null;
  browserId: string | null;
}

/**
 * Fetch user config (country, currency) from API
 * @returns Object with country and currency, or nulls on error
 */
export async function fetchMeConfig(): Promise<MeConfigResponse> {
  try {
    const response = await http().get(ME_CONFIG_ENDPOINT);
    const attributes = response.data?.data?.attributes;
    return {
      country: attributes?.country ?? null,
      currency: attributes?.currency ?? null,
      browserId: attributes?.browserId ?? null,
    };
  } catch (error) {
    captureException(new Error('Failed to fetch me/config from API', { cause: error }));
    return { country: null, currency: null, browserId: null };
  }
}

export interface CurrenciesRatesResponse {
  date: string | null;
  rates: Record<string, Record<string, number>> | null;
}

/**
 * Fetch latest currency rates from API
 * Transforms JSON:API array format to currenciesRates object format
 * @returns Object with date and rates, or nulls on error
 */
export async function fetchCurrenciesRates(): Promise<CurrenciesRatesResponse> {
  try {
    const response = await http().get(CURRENCIES_RATES_ENDPOINT);
    const data = response.data?.data;

    if (!data || !Array.isArray(data) || data.length === 0) {
      return { date: null, rates: null };
    }

    const rates: Record<string, Record<string, number>> = {};
    let date: string | null = null;

    data.forEach((item: { attributes?: { currencyFrom?: string; currencyTo?: string; rate?: number; date?: string } }) => {
      const { currencyFrom, currencyTo, rate, date: itemDate } = item.attributes || {};
      if (!date) {
        date = itemDate;
      }
      if (currencyFrom && currencyTo && typeof rate === 'number') {
        if (!rates[currencyFrom]) {
          rates[currencyFrom] = {};
        }
        rates[currencyFrom][currencyTo] = rate;
      }
    });

    return { date, rates };
  } catch (error) {
    captureException(new Error('Failed to fetch currencies rates from API', { cause: error }));
    return { date: null, rates: null };
  }
}

export interface AuthIdentityAccount {
  id: string;
  type: 'brand' | 'retailer';
  email: string;
}

export interface AuthIdentityAks {
  entity: 'guest' | 'user' | 'admin';
  account?: AuthIdentityAccount;
}

export interface AuthIdentityResponse {
  isAuthenticated: boolean;
  entity: AuthIdentityAks['entity'] | null;
  accountType: AuthIdentityAccount['type'] | null;
}

/**
 * Fetch auth identity to check if user is authenticated (not a guest)
 * @returns Object with isAuthenticated flag and account info
 */
export async function fetchAuthIdentity(): Promise<AuthIdentityResponse> {
  try {
    const response = await http().get(AUTH_IDENTITY_ENDPOINT);
    const aks: AuthIdentityAks | undefined = response.data?.aks;
    const entity = aks?.entity ?? null;
    return {
      isAuthenticated: entity !== null && entity !== 'guest',
      entity,
      accountType: aks?.account?.type ?? null,
    };
  } catch (error) {
    captureException(new Error('Failed to fetch auth/identity from API', { cause: error }));
    return { isAuthenticated: false, entity: null, accountType: null };
  }
}

export type CategoriesResponse = Record<string, string> | null;

export async function fetchCategories(): Promise<CategoriesResponse> {
  try {
    const response = await http().get(BRAND_CATEGORIES_ENDPOINT);
    return response.data?.data ?? null;
  } catch (error) {
    captureException(new Error('Failed to fetch brand categories from API', { cause: error }));
    return null;
  }
}

export interface ManagedLanguageConfig {
  language_label: string;
  language_flag_emoji: string;
  language_flag_country_iso: string;
  prismic_locale: string;
  faq_locale: string;
  new_faq_locale: string;
}

export type ManagedLanguagesResponse = Record<string, ManagedLanguageConfig> | null;
export type GlobalConfigResponse = Record<string, unknown> | null;

export interface GlobalConfigDataResponse {
  globalConfig: GlobalConfigResponse;
  managedLanguages: ManagedLanguagesResponse;
  imgCdn: string | null;
  sentryDsn: string | null;
  environment: string | null;
}

/**
 * Fetch global config from API (single call for globalConfig, managedLanguages, and imgCdn)
 * @returns Object with globalConfig, managedLanguages, and imgCdn, or nulls on error
 */
export async function fetchGlobalConfigData(): Promise<GlobalConfigDataResponse> {
  try {
    const response = await http().get(GLOBAL_CONFIG_ENDPOINT);
    const data = response.data?.data ?? null;
    return {
      globalConfig: data,
      managedLanguages: data?.managed_languages ?? null,
      imgCdn: data?.img_cdn ?? null,
      sentryDsn: data?.sentry_frontend_dsn ?? null,
      environment: data?.environment ?? null,
    };
  } catch (error) {
    captureException(new Error('Failed to fetch global config from API', { cause: error }));
    return { globalConfig: null, managedLanguages: null, imgCdn: null, sentryDsn: null, environment: null };
  }
}
